import { useTranslation } from 'module/hooks/use-translation';
import { Alert } from 'module/ui/components/atoms/alert';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { Argon } from 'module/ui/components/templates/argon';
import {
  faPlus,
  // faChevronDown,
  faFileLines,
} from '@fortawesome/pro-regular-svg-icons';
// import { faBook, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useNavigate } from '@rocketlane/rocket-router';
import { Button } from '@rocketlane/ui';
import { PageTemplateLite, SourceType } from 'api/page-template/types';
import {
  useDeleteMeetingPageTemplateMutation,
  useGetMeetingPageTemplatesWithScrollPagination,
  useUpdateMeetingPageTemplateOrderMutation,
  useUpdateMeetingPageTemplateStatusMutation,
} from 'queries/meeting-page-template';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ROUTE_PATHS } from 'routes/route-paths';
// import styled from 'styled-components';
import { FlexWrapperWithShortGap } from 'components/common/styles';
import {
  PageTemplatesNoSearchMatchState,
  PageTemplatesNoTemplatesState,
} from 'components/organisms/document/shared/empty-state';
import { TemplateTable } from 'components/organisms/pages-template/shared/page-template-table';
import { ContentWrapper, ListLoader } from '../styles';

const MEETING_TEMPLATES_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export type MeetingTemplatePermissions = {
  canRead: boolean;
  canCreate: boolean;
  canWrite: boolean;
  canDelete: boolean;
};

export const MeetingPageTemplates = ({
  meetingTemplatePermissions,
  isModuleFed = true,
}: {
  meetingTemplatePermissions?: MeetingTemplatePermissions;
  isModuleFed?: boolean;
} = {}) => {
  const title = <FormattedMessage id="meeting_page_templates.title" />;
  const navigate = useNavigate();
  const perms = isModuleFed ? meetingTemplatePermissions : undefined;
  const canCreate = perms?.canCreate !== false;
  const canRead = perms?.canRead !== false;
  const canWrite = perms?.canWrite !== false;
  const canDelete = perms?.canDelete !== false;
  const templateTablePermissions =
    perms != null ? { canRead, canWrite, canCreate, canDelete } : undefined;
  const { value: searchQuery, onChange: onSearchChange } =
    useSearchValueState();
  const searchInputRef = useRef<ExpandableSearchInputRef>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    templates: apiTemplates,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetMeetingPageTemplatesWithScrollPagination({
    limit: MEETING_TEMPLATES_PAGE_SIZE,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  });
  const [templates, setTemplates] = useState<PageTemplateLite[]>([]);
  const deleteMeetingPageTemplateMutation =
    useDeleteMeetingPageTemplateMutation();

  const updateMeetingPageTemplateOrderMutation =
    useUpdateMeetingPageTemplateOrderMutation();

  const updateMeetingPageTemplateStatusMutation =
    useUpdateMeetingPageTemplateStatusMutation();

  useEffect(() => {
    setTemplates(apiTemplates);
  }, [apiTemplates]);

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleDelete = async (id: string) => {
    try {
      await deleteMeetingPageTemplateMutation.mutateAsync(Number(id));
    } catch (error) {
      // toast.error(error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleStatusChange = async (id: string) => {
    setTemplates(
      templates.map((template) =>
        String(template.templateId) === id
          ? { ...template, enabled: !template.enabled }
          : template,
      ),
    );
    await updateMeetingPageTemplateStatusMutation.mutateAsync({
      templateId: Number(id),
      status: !templates.find((template) => String(template.templateId) === id)
        ?.enabled,
    });
  };

  const handleReorder = async (
    newTemplates: PageTemplateLite[],
    movedTemplateId: number,
    newIndex: number,
  ) => {
    try {
      setTemplates(newTemplates);
      await updateMeetingPageTemplateOrderMutation.mutateAsync({
        templateId: movedTemplateId,
        // Loaded pages are ordered from offset 0, so local index == absolute order index
        order: newIndex,
      });
    } catch (error) {
      // toast.error(error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const hasActiveSearch = Boolean(debouncedSearch);
  const showEmptyNoTemplates = !hasActiveSearch && total === 0;
  const showEmptyNoSearchMatch = hasActiveSearch && total === 0;

  return (
    <Argon
      documentTitle={useTranslation('meeting_page_templates.title')}
      title={
        <HeaderWithIconTitle
          icon={<FontAwesomeIcon icon={faFileLines} size={20} />}
          title={title}
        />
      }
      description={<FormattedMessage id="meeting_page_templates.description" />}
      actions={{
        secondary: (
          <FlexWrapperWithShortGap>
            <ExpandableSearchInput
              ref={searchInputRef}
              direction="right"
              value={searchQuery}
              onChange={onSearchChange}
              searchButtonProps={{
                type: 'text',
              }}
              minWidth="360px"
            />
            {/* <Button>
              <FlexWrapperWithShortGap>
                <StyledBookIcon icon={faBook} />
                <FontAwesomeIcon icon={faChevronDown} />
              </FlexWrapperWithShortGap>
            </Button> */}

            {canWrite && (
              <Button
                variant="primary"
                type="button"
                leftIcon={<FontAwesomeIcon icon={faPlus} size={12} />}
                onClick={() => navigate(ROUTE_PATHS.NEW)}
              >
                <FormattedMessage id="new_template" />
              </Button>
            )}
          </FlexWrapperWithShortGap>
        ),
      }}
      content={
        isLoading ? (
          <ListLoader />
        ) : (
          <ContentWrapper>
            {showEmptyNoTemplates ? (
              <PageTemplatesNoTemplatesState
                sourceType={SourceType.Meeting}
                onPrimaryAction={
                  canWrite ? () => navigate(ROUTE_PATHS.NEW) : () => {}
                }
              />
            ) : showEmptyNoSearchMatch ? (
              <PageTemplatesNoSearchMatchState
                sourceType={SourceType.Meeting}
                onPrimaryAction={() =>
                  searchInputRef.current?.closeSearchInput()
                }
              />
            ) : (
              <>
                <Alert
                  type="info"
                  icon={faInfoCircle}
                  content={
                    <FormattedMessage id="meeting_page_templates.info.content" />
                  }
                />

                <TemplateTable
                  templates={templates}
                  onStatusChange={handleStatusChange}
                  onEdit={(id: string) => {
                    navigate(`${id}`);
                  }}
                  onDelete={handleDelete}
                  templatePermissions={templateTablePermissions}
                  isModuleFed={isModuleFed}
                  onReorder={handleReorder}
                  loading={isLoading}
                  loadingMore={isFetchingNextPage}
                  hasMore={hasNextPage}
                  onLoadMore={handleLoadMore}
                />
              </>
            )}
          </ContentWrapper>
        )
      }
    />
  );
};
